/**
 * Boot + wiring only. No feature logic lives here — that is the rule for this
 * file for the whole project, and it is easiest to keep from the start.
 *
 * Eight panels, all of which stay useful past the phase that introduced them:
 *
 *   1. Vision — the camera, the landmarks the maths actually reads, and the
 *      meters Phase 1 is accepted on.
 *   2. Transcript — words bound to the moment they were spoken, and the diff log
 *      that shows the revision handling working. Phase 2's acceptance test.
 *   3. Audio — the features Channel B classifies on, with their extremes held so
 *      a ten-millisecond transient is readable. Phase 4's acceptance test.
 *   4. Trills — the envelope's modulation, and the per-window rate history that
 *      shows why a stable peak is the only thing that means anything.
 *   5. The recorder — labelled sessions, pushed to R2. Every later phase would
 *      otherwise be retuned by describing a face over chat.
 *   6. The controls — every threshold in the project, live. Phase 8, and the
 *      panel that makes the other seven adjustable while you are in front of
 *      them rather than between sessions.
 *   7. The ramp — visual proof that `sampleAt` interpolates and clamps. The flat
 *      shoulders are what a stalled camera will look like.
 *   8. The timeline — events released in spoken order regardless of the order
 *      they were detected in. This is how LAG gets judged, and since Phase 5 the
 *      events arriving in it are real.
 */

import '@fontsource-variable/roboto-flex/full.css';
import './style.css';

import { config } from './core/config.js';
import { sessionKey } from './core/recorder.js';
// Panel 7 builds its own buffer for the acceptance ramp — the only RingBuffer on
// this page the engine does not own.
import { RingBuffer } from './core/ringbuffer.js';
import { applyTuning, decodeTuning, encodeTuning, tuning } from './core/tunables.js';
import {
  audioSupported,
  Engine,
  recognitionSupported,
  uploadSession,
  visionSupported,
} from './engine.js';
import { Caption } from './render/caption.js';
import { downloadCaptionSVG } from './render/exportsvg.js';
import { Trace } from './render/trace.js';
import { Controls } from './ui/controls.js';
import { Guide } from './ui/guide.js';
import { AUDIO_METERS, MOUTH_METERS, Readout, TRILL_METERS } from './ui/readout.js';

// ─── Tuning, before anything reads a threshold ──────────────────────────────

/**
 * Apply the tuning carried in the URL fragment, first thing.
 *
 * Ahead of every other line in this file on purpose. Detectors read config at
 * use time so most of it could be applied whenever, but the buffers below are
 * sized once and a slider that only took effect on the next reload would be a
 * slider nobody could trust.
 *
 * The fragment is also the only place a tuning lives. Not localStorage: nothing
 * in this project is left on the machine after the tab closes, and a set of
 * thresholds is not the exception that starts that. The address bar is honest
 * about what it is holding, survives a reload, opens on the phone, and can be
 * pasted into a message — which is how a number found in a session gets back
 * into config.js.
 */
const startupTuning = applyTuning(decodeTuning(window.location.hash));

const video = document.getElementById('video');
const stage = document.querySelector('.stage');
const overlay = document.getElementById('overlay');
const octx = overlay.getContext('2d');
const camBtn = document.getElementById('cam');
const camState = document.getElementById('cam-state');
const visionNote = document.getElementById('vision-note');
const faceState = document.getElementById('face-state');
const readout = new Readout(document.getElementById('meters'), MOUTH_METERS);

/**
 * The machine. Identical to the one `/perform` runs — see engine.js.
 *
 * This page owns none of it: it subscribes, draws meters, and stays out of the
 * way. Everything below destructures the engine's parts into the names the
 * panels have always used, so a panel reads the same as it did when this file
 * built the whole thing itself.
 */
const engine = new Engine({
  video,
  onState: (channel, state, detail) => onChannelState(channel, state, detail),
  onResult: (diff, result) => {
    renderLine();
    logDiff(diff, result.text, result.isFinal);
  },
  onGesture: (event) => logGesture(event),
  onRelease: (event) => onRelease(event),
  onFrame: (ctx) => onFrame(ctx),
});

const { mouthBuffer, capture, landmarker, recognition, recorder, timeline, transcript } = engine;

// ── Feed / points visibility ────────────────────────────────────────────────
// Independent toggles rather than one three-way control: the useful views are
// points-on-video for checking a landmark sits where it should, and points
// alone for reading motion without a face in the way.
const showFeed = document.getElementById('show-feed');
const showPoints = document.getElementById('show-points');

function syncStage() {
  stage.classList.toggle('hide-feed', !showFeed.checked);
  stage.classList.toggle('hide-points', !showPoints.checked);
}
showFeed.addEventListener('change', syncStage);
showPoints.addEventListener('change', syncStage);
syncStage();

/**
 * Draw only the landmarks the maths actually reads, with the distances it
 * derives from them.
 *
 * A full 478-point mesh looks impressive and tells you nothing. Seven points and
 * three lines tell you immediately whether index 13 is on the inner upper lip or
 * somewhere near an eyebrow — which is the failure the spec warns about, and it
 * never throws, it just produces a number that does not move.
 */
function drawOverlay(landmarks) {
  // Match the camera's real aspect rather than assuming 4:3. Plenty of webcams
  // hand back 16:9 whatever you ask for, and with the stage fixed at 4:3 the
  // video gets cropped while the overlay does not — putting every dot a
  // believable but wrong distance from the feature it is supposed to mark.
  const vw = video.videoWidth;
  const vh = video.videoHeight;
  if (vw && vh && (overlay.width !== vw || overlay.height !== vh)) {
    overlay.width = vw;
    overlay.height = vh;
    stage.style.aspectRatio = `${vw} / ${vh}`;
  }

  const w = overlay.width;
  const h = overlay.height;
  octx.clearRect(0, 0, w, h);
  if (!landmarks) return;

  const { idx } = config.vision;
  const at = (i) => ({ x: landmarks[i].x * w, y: landmarks[i].y * h });

  const line = (a, b, colour) => {
    octx.strokeStyle = colour;
    octx.lineWidth = 1.5;
    octx.beginPath();
    octx.moveTo(a.x, a.y);
    octx.lineTo(b.x, b.y);
    octx.stroke();
  };

  line(at(idx.eyeL), at(idx.eyeR), 'rgba(232,230,225,0.45)'); // iod — the scale reference
  line(at(idx.cornerL), at(idx.cornerR), '#d8a657'); // width
  line(at(idx.lipUpperInner), at(idx.lipLowerInner), '#e07a5f'); // aperture

  const points = [
    [idx.eyeL, 'rgba(232,230,225,0.6)'],
    [idx.eyeR, 'rgba(232,230,225,0.6)'],
    [idx.cornerL, '#d8a657'],
    [idx.cornerR, '#d8a657'],
    [idx.lipUpperInner, '#e07a5f'],
    [idx.lipLowerInner, '#e07a5f'],
    [idx.lipUpperOuter, '#a3c585'], // lipJitter
  ];
  for (const [i, colour] of points) {
    const p = at(i);
    octx.fillStyle = colour;
    octx.beginPath();
    octx.arc(p.x, p.y, 3, 0, Math.PI * 2);
    octx.fill();
  }
}

camBtn.addEventListener('click', async () => {
  if (landmarker.state === 'running') {
    landmarker.stop();
    drawOverlay(null);
    readout.update(null);
    return;
  }
  camBtn.disabled = true;
  try {
    await landmarker.start();
  } catch {
    // onState has already reported it; the note line below carries the detail.
  } finally {
    camBtn.disabled = false;
  }
});

document.getElementById('cam-reset').addEventListener('click', () => {
  landmarker.features.reset();
});

if (!visionSupported()) {
  camBtn.disabled = true;
  camState.textContent = 'unavailable';
  visionNote.className = 'assert mono fail';
  visionNote.textContent = window.isSecureContext
    ? 'getUserMedia is not available in this browser.'
    : 'Camera needs a secure context — use https or localhost, not a bare LAN address.';
}

/**
 * The status line under the meters: frame rate, delegate, calibration progress,
 * and — loudest of all — any blendshape the model did not supply.
 */
function updateVisionNote() {
  const { stats, features } = landmarker;
  if (features.missingShapes.length) {
    visionNote.className = 'assert mono fail';
    visionNote.textContent = `blendshapes not supplied by the model: ${features.missingShapes.join(', ')}`;
    return;
  }

  const cal = features.calibration;
  const seen = stats.frames ? Math.round((stats.faces / stats.frames) * 100) : 0;
  visionNote.className = 'assert mono';
  visionNote.textContent =
    `${stats.fps} fps · ${stats.delegate} · face in ${seen}% of frames · ` +
    (cal.ready
      ? `width calibrated over ${cal.span.toFixed(3)}`
      : `width calibrating, span ${cal.span.toFixed(3)} of ${config.vision.WIDTH_MIN_SPAN}`);
}

// ─── Panel 2: transcript ────────────────────────────────────────────────────

const asrBtn = document.getElementById('asr');
const asrState = document.getElementById('asr-state');
const asrNote = document.getElementById('asr-note');
const lineEl = document.getElementById('line');
const diffLogEl = document.getElementById('difflog');
const bindingsEl = document.getElementById('bindings');

/** Wall-clock zero for the readouts, shared with the timeline panel. */
const t0 = performance.now();

/**
 * Every channel's state, in one place.
 *
 * The engine reports `(channel, state, detail)` rather than giving each device
 * its own callback, because the page has to say the same three things about all
 * of them — what it is doing, whether that is a problem, and whether recording
 * is still possible. `face` is not a device: it is the engine noticing that
 * frames are arriving with nobody in them.
 */
function onChannelState(channel, state, detail) {
  const text = detail ? `${state} · ${detail}` : state;

  if (channel === 'camera') {
    camState.textContent = text;
    camBtn.textContent = state === 'running' ? 'stop camera' : 'start camera';
    syncRecorderControls();
  } else if (channel === 'mic') {
    micState.textContent = text;
    micBtn.textContent = capture.active ? 'stop microphone' : 'start microphone';
    if (state === 'error') {
      audioNote.className = 'assert mono fail';
      audioNote.textContent = `microphone unavailable: ${detail}`;
    }
    syncRecorderControls();
  } else if (channel === 'asr') {
    asrState.textContent = text;
    asrBtn.textContent = recognition.listening ? 'stop listening' : 'start listening';
    if (state === 'error') {
      asrNote.className = 'assert mono fail';
      asrNote.textContent = `speech recognition stopped: ${detail}`;
    }
  } else if (channel === 'face') {
    // Distinct from the camera watchdog, and the distinction is the point: the
    // camera is fine, there is simply nobody in front of it. A take lost its
    // face for forty of sixty-nine seconds this way while the speaker read the
    // screen, and every gesture in that hole was judged with no vision at all.
    faceState.textContent = state === 'lost' ? `no face for ${detail}` : 'face found';
    faceState.className = state === 'lost' ? 'mono warn' : 'mono dim';
  }
}

const caption = new Caption(lineEl);
const trace = new Trace(document.getElementById('trace'));

function renderLine() {
  caption.render(transcript);
  renderBindings();
}

function renderBindings() {
  const words = transcript.words.slice(-14);
  bindingsEl.replaceChildren(
    ...(words.length
      ? words.map((w) =>
          row(
            `${w.isFinal ? '·' : '◦'} ${w.text}`,
            `${((w.tBind - t0) / 1000).toFixed(2)}s +${w.dur | 0}ms`,
          ),
        )
      : [emptyRow('nothing bound yet')]),
  );
}

let diffCount = 0;

function logDiff(diff, text, isFinal) {
  diffCount++;
  const parts = [`kept ${diff.kept}`];
  if (diff.added) parts.push(`+${diff.added}`);
  if (diff.dropped) parts.push(`-${diff.dropped}`);
  if (diff.finalised) parts.push(`froze ${diff.finalised}`);

  const li = row(
    `${String(diffCount).padStart(3, '0')} ${isFinal ? 'FINAL' : 'interim'}`,
    parts.join(' · '),
  );
  li.title = text;
  diffLogEl.prepend(li);
  while (diffLogEl.childElementCount > 60) diffLogEl.lastElementChild.remove();
}

asrBtn.addEventListener('click', () => {
  if (recognition.listening) recognition.stop();
  else recognition.start();
  asrBtn.textContent = recognition.listening ? 'stop listening' : 'start listening';
});

document.getElementById('asr-clear').addEventListener('click', () => {
  transcript.reset();
  caption.clear();
  diffCount = 0;
  diffLogEl.replaceChildren();
  renderLine();
});

document.getElementById('asr-export').addEventListener('click', async (e) => {
  const btn = e.currentTarget;
  btn.disabled = true;
  const previous = btn.textContent;
  btn.textContent = 'exporting…';
  try {
    const bytes = await downloadCaptionSVG(caption);
    asrNote.className = 'assert mono pass';
    asrNote.textContent = `exported ${(bytes / 1024).toFixed(0)} kB of SVG, font embedded`;
  } catch (err) {
    asrNote.className = 'assert mono fail';
    asrNote.textContent = String(err.message ?? err);
  } finally {
    btn.disabled = false;
    btn.textContent = previous;
  }
});

// The second LAG slider lives beside the trace, where it is actually used —
// the timeline panel has one too, and both drive the same config field.
const lag2 = document.getElementById('lag2');
const lag2out = document.getElementById('lag2out');
lag2.min = config.LAG_MIN;
lag2.max = config.LAG_MAX;
lag2.value = config.LAG;
lag2.addEventListener('input', () => controls.set('LAG', Number(lag2.value)));

if (!recognitionSupported()) {
  asrBtn.disabled = true;
  asrState.textContent = 'unavailable';
  asrNote.className = 'assert mono fail';
  asrNote.textContent =
    'This browser has no SpeechRecognition. Channel A needs Chrome or Edge; ' +
    'the vision and audio channels work anywhere.';
}

diffLogEl.replaceChildren(emptyRow('no results yet'));
renderLine();

// ─── Panel 3: audio features ────────────────────────────────────────────────

const audioReadout = new Readout(document.getElementById('audio-meters'), AUDIO_METERS);
const micBtn = document.getElementById('mic');
const micState = document.getElementById('mic-state');
const audioNote = document.getElementById('audio-note');
const checksEl = document.getElementById('audio-checks');

micBtn.addEventListener('click', async () => {
  if (capture.active) {
    capture.stop();
    audioReadout.update(null);
    return;
  }
  micBtn.disabled = true;
  try {
    await capture.start();
  } catch {
    // onState reported it; audioNote carries the detail.
  } finally {
    micBtn.disabled = false;
  }
});

document.getElementById('mic-reset').addEventListener('click', () => audioReadout.resetPeaks());

/**
 * The three criteria Phase 4 is accepted on, read off the held extremes rather
 * than off a bar somebody watched go past.
 */
const CHECKS = [
  {
    label: 'whistle → flatness < 0.15',
    key: 'flatness',
    read: (e) => e.lo,
    ok: (v) => v < 0.15,
    format: (v) => v.toFixed(3),
  },
  {
    label: 'sustained "sss" → zcr > 0.6',
    key: 'zcr',
    read: (e) => e.hi,
    ok: (v) => v > 0.6,
    format: (v) => v.toFixed(3),
  },
  {
    label: 'hand clap → attack > 15 dB',
    key: 'attack',
    read: (e) => e.hi,
    ok: (v) => v > 15,
    format: (v) => `${v.toFixed(1)} dB`,
  },
];

const checkRows = CHECKS.map((c) => {
  const li = row(c.label, '—');
  checksEl.append(li);
  return { ...c, meta: li.lastElementChild };
});

function renderChecks() {
  for (const c of checkRows) {
    const v = c.read(audioReadout.extremes(c.key));
    if (v === null) {
      c.meta.textContent = 'not attempted';
      c.meta.className = 'meta';
      continue;
    }
    c.meta.textContent = `${c.format(v)} ${c.ok(v) ? 'PASS' : 'not yet'}`;
    c.meta.className = `meta row-status ${c.ok(v) ? 'ok' : ''}`;
  }
}

/**
 * The status line: the rates everything downstream depends on, and — loudest of
 * all — any capture constraint the browser decided not to honour.
 */
function updateAudioNote() {
  const s = capture.stats;
  if (s.stalled) {
    // The failure mode this exists for produced no error and no silence — it
    // produced twenty-three seconds of the same number. Say it plainly.
    audioNote.className = 'assert mono fail';
    audioNote.textContent =
      `AUDIO STALLED — the graph has stopped being fed${s.contextState ? ` (context ${s.contextState})` : ''}. ` +
      'Usually the speech recogniser taking the microphone. Recovering; ' +
      'no features are being recorded meanwhile.';
    return;
  }
  if (s.ignored.length) {
    audioNote.className = 'assert mono fail';
    audioNote.textContent = `capture constraints ignored — ${s.ignored.join('; ')}`;
    return;
  }
  const parts = [
    `${(s.sampleRate / 1000).toFixed(1)} kHz`,
    `envelope ${s.envelopeRate.toFixed(1)} Hz`,
    `block ${s.blockSize}`,
    `clock offset ${s.clockOffset === null ? '—' : `${s.clockOffset.toFixed(1)} ms`}`,
  ];
  if (s.recoveries) parts.push(`${s.recoveries} stall(s) recovered`);
  if (s.unreported.length) parts.push(`not reported: ${s.unreported.join(', ')}`);
  audioNote.className = 'assert mono';
  audioNote.textContent = parts.join(' · ');
}

if (!audioSupported()) {
  micBtn.disabled = true;
  micState.textContent = 'unavailable';
  audioNote.className = 'assert mono fail';
  audioNote.textContent = window.isSecureContext
    ? 'This browser has no AudioWorklet. Channel B cannot run here.'
    : 'The microphone needs a secure context — use https or localhost, not a bare LAN address.';
}

renderChecks();

// ─── Panel 4: trills ────────────────────────────────────────────────────────

const trillReadout = new Readout(document.getElementById('trill-meters'), TRILL_METERS);
const trillLogEl = document.getElementById('trill-log');
const trillHopsEl = document.getElementById('trill-hops');
const trillNote = document.getElementById('trill-note');

let gestureCount = 0;

/**
 * A gesture survived classification and the vision gate.
 *
 * The engine has already put it on the timeline with its own `detectLatency`,
 * exactly as a transcript word is; this only logs it. Whether the camera could
 * see a face at the time is the difference between a confidence of 0.60 meaning
 * "no camera" and it meaning "camera on, nobody in front of it" — so it is on
 * the row.
 */
function logGesture(event) {
  gestureCount++;
  const blind = engine.faceLost ? ' · no face' : '';
  const place = event.place && event.place !== 'unknown' ? ` · ${event.place}` : '';
  trillLogEl.prepend(
    row(
      `${String(gestureCount).padStart(3, '0')} ${event.type}${place}`,
      `${event.modRate ? `${event.modRate.toFixed(1)} Hz · ${event.cycles} cycles · ` : ''}` +
        `${event.dur | 0} ms · conf ${event.confidence.toFixed(2)}${blind}`,
    ),
  );
  while (trillLogEl.childElementCount > 40) trillLogEl.lastElementChild.remove();
}

/** The per-window history, which is where a real trill is visible as a flat run. */
function renderTrillHops() {
  const hops = capture.envelope?.hops ?? [];
  const recent = hops.slice(-10).reverse();
  trillHopsEl.replaceChildren(
    ...(recent.length
      ? recent.map((h) =>
          row(
            `${h.trill ? '●' : '·'} ${h.modRate.toFixed(1)} Hz`,
            `stab ${h.stability.toFixed(2)} · ratio ${h.ratio.toFixed(2)} · depth ${h.depth.toFixed(3)}`,
          ),
        )
      : [emptyRow('start the microphone — the first window needs 683 ms')]),
  );
}

function updateTrillNote() {
  const e = capture.envelope;
  const s = capture.stats;
  trillNote.className = 'assert mono';
  trillNote.textContent =
    `band ${config.envelope.BAND_LO}–${config.envelope.BAND_HI} Hz · ` +
    `bin ${e.binHz.toFixed(2)} Hz · window ${e.windowMs.toFixed(0)} ms · ` +
    `floor ${s.noiseFloorDb === null ? '—' : `${s.noiseFloorDb.toFixed(1)} dB`} · ` +
    `gate ${s.gateDb === null ? '—' : `${s.gateDb.toFixed(1)} dB`}`;
}

trillLogEl.replaceChildren(emptyRow('nothing detected yet'));
renderTrillHops();

// ─── Panel 5: the session recorder ──────────────────────────────────────────

const recBtn = document.getElementById('rec');
const recAllBtn = document.getElementById('rec-all');
const recState = document.getElementById('rec-state');
const recNote = document.getElementById('rec-note');
const recNoteLine = document.getElementById('rec-note-line');
const marksEl = document.getElementById('marks');
const markN = document.getElementById('mark-n');
const sessionsEl = document.getElementById('sessions');
const markButtons = [
  ...document.querySelectorAll('[data-mark]'),
  document.getElementById('mark-custom'),
];

/**
 * Enable only what makes sense in the current state.
 *
 * Either channel is enough to record. A microphone-only take is how a trill or
 * a raspberry gets captured for Phase 5, and there is no reason to make somebody
 * open the camera to make one.
 */
function syncRecorderControls() {
  // `active` rather than `running`: a stalled microphone is still open, and a
  // recording in progress must not be disarmed because the audio graph is
  // being recovered.
  const live = landmarker.active || capture.active;
  recBtn.disabled = !live && !recorder.recording;
  recBtn.textContent = recorder.recording ? 'stop' : 'record';
  recBtn.classList.toggle('recording', recorder.recording);
  recAllBtn.textContent = recorder.recording ? 'stop everything' : 'record everything';
  recAllBtn.classList.toggle('recording', recorder.recording);
  for (const b of markButtons) b.disabled = !recorder.recording;
  if (!recorder.recording && !live) recState.textContent = 'nothing capturing';
}

function renderMarks() {
  const marks = recorder.marks;
  markN.textContent = String(marks.length);
  marksEl.replaceChildren(
    ...(marks.length
      ? marks
          .slice()
          .reverse()
          .map((m) => row(m.label, `${(m.t / 1000).toFixed(2)} s`))
      : [emptyRow('record, then label what you are doing')]),
  );
}

function addMark(label) {
  if (!recorder.recording) return;
  recorder.mark(label);
  renderMarks();
}

for (const btn of document.querySelectorAll('[data-mark]')) {
  btn.addEventListener('click', () => addMark(btn.dataset.mark));
}
document.getElementById('mark-custom').addEventListener('click', () => {
  const label = prompt('Label for this moment');
  if (label) addMark(label.trim());
});

recNote.addEventListener('input', () => recorder.setNote(recNote.value.trim()));

/**
 * Start a take from the lab's own controls.
 *
 * The engine owns the recorder, the wake lock and the environment snapshot; this
 * only decides what the panel says about it.
 */
function startRecording() {
  engine.startRecording(recNote.value.trim());
  renderMarks();
  recNoteLine.className = 'assert mono';
  recNoteLine.textContent = 'recording…';
  syncRecorderControls();
}

function stopRecording() {
  const session = engine.stopRecording();
  renderMarks();
  if (!session) {
    recNoteLine.className = 'assert mono';
    recNoteLine.textContent = 'nothing recorded';
  } else {
    recNoteLine.className = 'assert mono';
    recNoteLine.textContent =
      `recorded ${(session.durationMs / 1000).toFixed(1)} s · ` +
      `${session.columns.t.length} mouth @ ${session.sampleRateHz} Hz · ` +
      `${session.audio.t.length} audio @ ${session.audioRateHz} Hz · ` +
      `${session.envelope.t.length} envelope @ ${session.envelopeRateHz} Hz`;
    // Every recording goes to R2. Uploading is not a decision worth making one
    // take at a time, and a recording that only exists in a tab is a recording
    // that is about to be lost.
    const rowEl = addSessionRow(session);
    sendSession(session, rowEl);
  }
  syncRecorderControls();
}

recBtn.addEventListener('click', () => {
  if (recorder.recording) stopRecording();
  else startRecording();
});

/**
 * One button for a take: every capture on, recording started, and all of it
 * stopped together. The sequencing lives in the engine, because the piece needs
 * exactly the same thing and getting it subtly different on two pages would make
 * their recordings incomparable.
 */
async function startEverything() {
  const failures = await engine.startAll({ note: recNote.value.trim() });
  renderMarks();
  recNoteLine.className = failures.length ? 'assert mono fail' : 'assert mono';
  recNoteLine.textContent = failures.length
    ? `recording without: ${failures.join(' · ')}`
    : 'recording…';
}

function stopEverything() {
  const session = engine.stopAll();
  renderMarks();
  if (session) {
    recNoteLine.className = 'assert mono';
    recNoteLine.textContent =
      `recorded ${(session.durationMs / 1000).toFixed(1)} s · ` +
      `${session.columns.t.length} mouth · ${session.audio.t.length} audio`;
    sendSession(session, addSessionRow(session));
  }
  drawOverlay(null);
  readout.update(null);
  audioReadout.update(null);
}

recAllBtn.addEventListener('click', async () => {
  recAllBtn.disabled = true;
  try {
    if (recorder.recording) stopEverything();
    else await startEverything();
  } finally {
    recAllBtn.disabled = false;
    syncRecorderControls();
  }
});

/**
 * One row per finished session: its key, its upload status, and a download as
 * the escape hatch for when the network is not cooperating.
 *
 * Sessions live in memory for the life of the tab and nowhere else. They are
 * recordings of somebody's face; parking them in browser storage would leave
 * them on the machine long after the reason for making them had passed.
 */
function addSessionRow(session) {
  const li = document.createElement('li');

  const label = document.createElement('span');
  label.className = 'label';
  label.textContent = sessionKey(session);

  const actions = document.createElement('span');
  actions.className = 'meta row-actions';

  const status = document.createElement('span');
  status.className = 'row-status';
  status.textContent = 'uploading…';

  const retry = document.createElement('button');
  retry.type = 'button';
  retry.textContent = 'retry';
  retry.hidden = true;
  retry.addEventListener('click', () => sendSession(session, li));

  const dl = document.createElement('button');
  dl.type = 'button';
  dl.textContent = 'download';
  dl.addEventListener('click', () => downloadSession(session));

  actions.append(status, retry, dl);
  li.append(label, actions);
  li._status = status;
  li._retry = retry;

  if (sessionsEl.querySelector('.empty')) sessionsEl.replaceChildren();
  sessionsEl.prepend(li);
  return li;
}

/** Upload, reporting into the row rather than into a modal. */
async function sendSession(session, rowEl) {
  rowEl._status.textContent = 'uploading…';
  rowEl._status.className = 'row-status';
  rowEl._retry.hidden = true;
  try {
    const res = await uploadSession(session);
    rowEl._status.textContent = `in R2 · ${(res.bytes / 1024).toFixed(0)} kB`;
    rowEl._status.className = 'row-status ok';
  } catch (err) {
    rowEl._status.textContent = String(err.message ?? err);
    rowEl._status.className = 'row-status bad';
    rowEl._retry.hidden = false;
    // The download button is the reason a failed upload is not a lost take.
    recNoteLine.className = 'assert mono fail';
    recNoteLine.textContent = `upload failed — download it instead: ${err.message ?? err}`;
  }
}

function downloadSession(session) {
  const blob = new Blob([JSON.stringify(session)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = sessionKey(session);
  a.click();
  URL.revokeObjectURL(url);
}

sessionsEl.replaceChildren(emptyRow('no recordings yet'));
renderMarks();
syncRecorderControls();

// ─── The guided take ─────────────────────────────────────────

const guideBtn = document.getElementById('guide');
const guideCue = document.getElementById('guide-cue');
const guideBar = document.getElementById('guide-bar');
const guideNote = document.getElementById('guide-note');

/**
 * The scripted take. See ui/guide.js for why marking by hand could not work.
 *
 * It marks through the engine's recorder, so a guided take is an ordinary
 * recording in every respect — it goes to R2, it replays through
 * `sessions:gestures`, and its marks are the ones the acceptance table reads.
 */
const guide = new Guide({
  onMark: (label) => {
    recorder.mark(label);
    renderMarks();
  },
  onDone: () => {
    // Stops the take as well as the script. The last thing on screen should be
    // a finished recording, not a page waiting for somebody to notice.
    if (recorder.recording) stopEverything();
    syncRecorderControls();
    guideBtn.textContent = 'guided take';
    guideNote.className = 'assert mono pass';
    guideNote.textContent = 'done — uploaded. Replay it with: npm run sessions:gestures';
  },
});

function renderGuide(state) {
  if (!state) {
    guideCue.textContent = '';
    guideCue.className = 'guide-cue';
    guideBar.style.width = '0%';
    return;
  }
  guideCue.textContent = state.step.text;
  guideCue.className = `guide-cue${state.active ? ' active' : ''}`;
  guideBar.style.width = `${state.overall * 100}%`;
}

guideBtn.addEventListener('click', async () => {
  if (guide.running) {
    guide.stop();
    if (recorder.recording) stopEverything();
    guideBtn.textContent = 'guided take';
    syncRecorderControls();
    return;
  }

  guideBtn.disabled = true;
  try {
    recNote.value = recNote.value.trim() || 'guided';
    await startEverything();
    // Started only once the devices are up, so the first cue is not spent
    // waiting for a camera permission dialog.
    guide.start(performance.now());
    guideBtn.textContent = 'stop';
    guideNote.className = 'assert mono';
    guideNote.textContent =
      `${(guide.durationMs / 1000 / 60).toFixed(1)} min · ` +
      'look at the camera and do what it says. Do not touch the screen.';
  } finally {
    guideBtn.disabled = false;
    syncRecorderControls();
  }
});

// ─── Panel 6: the controls ──────────────────────────────────────────────────

const tuneState = document.getElementById('tune-state');
const tuneNote = document.getElementById('tune-note');

/**
 * Write the current tuning into the address bar.
 *
 * Debounced, because a drag fires `input` on every pixel and sixty
 * `replaceState` calls a second is a thing browsers start rate-limiting. A
 * quarter of a second after the slider stops is soon enough for something whose
 * only consumer is a person deciding to copy the URL.
 *
 * `replaceState` rather than `hash =`: assigning the hash pushes a history
 * entry, and after an evening of tuning the back button would have to be pressed
 * four hundred times to leave the page.
 */
let urlTimer = 0;

function publishTuning() {
  clearTimeout(urlTimer);
  urlTimer = setTimeout(() => {
    const encoded = encodeTuning(tuning());
    history.replaceState(null, '', encoded ? `#${encoded}` : window.location.pathname);
    // The link to the piece carries the tuning with it, so crossing over does
    // not silently reset every threshold you just set by ear.
    document.getElementById('toperform').href = encoded ? `/perform#${encoded}` : '/perform';
  }, 250);
}

function updateTuneState() {
  const changed = Object.keys(tuning()).length;
  tuneState.textContent = changed
    ? `${changed} control${changed === 1 ? '' : 's'} moved · in the URL`
    : 'all at defaults';
  tuneState.className = changed ? 'mono' : 'mono dim';
}

const controls = new Controls(document.getElementById('tune'), {
  onChange: (field, value) => {
    publishTuning();
    updateTuneState();
    // A take is normally made *while* tuning, so a change during one is logged
    // with a timestamp. Without it, a recording whose second half ran under a
    // different threshold reads as a detector behaving inconsistently — and the
    // header's `tuning` only describes the moment record was pressed.
    recorder.event(`tune:${field.path}=${value}`);
    // LAG has two other sliders on this page. All three write one field, so
    // whichever moved, the other two are brought along.
    if (field.path === 'LAG') syncLagOutputs();
    if (field.path === 'speech.lang') relanguage();
  },
  onBulkChange: () => {
    publishTuning();
    updateTuneState();
    recorder.event('tune:reset');
    syncLagOutputs();
    relanguage();
  },
});

document.getElementById('tune-reset-all').addEventListener('click', () => controls.resetAll());

document.getElementById('tune-copy').addEventListener('click', async (e) => {
  const btn = e.currentTarget;
  const previous = btn.textContent;
  // Read from the encoder rather than from location.href, so the button works
  // during the debounce window instead of copying the previous tuning.
  const encoded = encodeTuning(tuning());
  const url = `${window.location.origin}${window.location.pathname}${encoded ? `#${encoded}` : ''}`;
  try {
    await navigator.clipboard.writeText(url);
    btn.textContent = 'copied';
  } catch {
    // Clipboard access is refused in plenty of legitimate situations. Putting
    // the URL in the note line is not a consolation prize — it is selectable.
    btn.textContent = previous;
    tuneNote.className = 'assert mono';
    tuneNote.textContent = url;
  }
  setTimeout(() => {
    btn.textContent = previous;
  }, 1200);
});

// Say so when a link arrives naming controls this build no longer has. A stale
// tuning still applies everything it got right, and silently dropping the rest
// would leave you tuning against thresholds you believed were set.
if (startupTuning.applied.length || startupTuning.ignored.length) {
  tuneNote.className = `assert mono ${startupTuning.ignored.length ? 'fail' : ''}`;
  tuneNote.textContent =
    `tuning from the URL: ${startupTuning.applied.length} applied` +
    (startupTuning.ignored.length ? ` · not recognised: ${startupTuning.ignored.join(', ')}` : '');
}

updateTuneState();

// ─── Panel 7: the ramp ──────────────────────────────────────────────────────

const SAMPLES = 100;
const SPACING = 10; // ms

/** The spec's acceptance ramp: sample i at t = i*10 with value i. */
const ramp = new RingBuffer(SAMPLES);
for (let i = 0; i < SAMPLES; i++) ramp.push({ t: i * SPACING, value: i });

const T_MIN = -200;
const T_MAX = 1200;
const V_MIN = -12;
const V_MAX = 112;

const canvas = document.getElementById('ramp');
const ctx = canvas.getContext('2d');

const x = (t) => ((t - T_MIN) / (T_MAX - T_MIN)) * canvas.width;
const y = (v) => canvas.height - ((v - V_MIN) / (V_MAX - V_MIN)) * canvas.height;

function drawRamp(queryT) {
  const w = canvas.width;
  const h = canvas.height;
  ctx.clearRect(0, 0, w, h);

  // The retained window, so the clamp regions are visibly outside something.
  const first = ramp.oldest().t;
  const last = ramp.latest().t;
  ctx.fillStyle = 'rgba(216, 166, 87, 0.05)';
  ctx.fillRect(x(first), 0, x(last) - x(first), h);

  // sampleAt swept across the full axis, well past both ends of the data.
  ctx.strokeStyle = '#d8a657';
  ctx.lineWidth = 2;
  ctx.beginPath();
  for (let px = 0; px <= w; px++) {
    const t = T_MIN + (px / w) * (T_MAX - T_MIN);
    const v = ramp.sampleAt(t).value;
    if (px === 0) ctx.moveTo(px, y(v));
    else ctx.lineTo(px, y(v));
  }
  ctx.stroke();

  // The stored samples themselves.
  ctx.fillStyle = 'rgba(232, 230, 225, 0.5)';
  for (const s of ramp.toArray()) {
    ctx.fillRect(x(s.t) - 1, y(s.value) - 1, 2.5, 2.5);
  }

  // The live query.
  const got = ramp.sampleAt(queryT);
  ctx.strokeStyle = '#e07a5f';
  ctx.lineWidth = 1;
  ctx.setLineDash([4, 4]);
  ctx.beginPath();
  ctx.moveTo(x(queryT), 0);
  ctx.lineTo(x(queryT), h);
  ctx.stroke();
  ctx.setLineDash([]);

  ctx.fillStyle = '#e07a5f';
  ctx.beginPath();
  ctx.arc(x(queryT), y(got.value), 4.5, 0, Math.PI * 2);
  ctx.fill();

  return got;
}

const qInput = document.getElementById('q');
const qOut = document.getElementById('qout');

function onQuery() {
  const t = Number(qInput.value);
  const got = drawRamp(t);
  const clamped = t < ramp.oldest().t || t > ramp.latest().t;
  qOut.textContent = `${got.value.toFixed(3)}${clamped ? '  (clamped)' : ''}`;
}

qInput.addEventListener('input', onQuery);

// The acceptance criterion, asserted in the page rather than only in vitest —
// so a deployed build that somehow shipped a broken sampleAt says so out loud.
const assertEl = document.getElementById('assert');
{
  const got = ramp.sampleAt(505).value;
  const ok = Math.abs(got - 50.5) < 0.01;
  assertEl.className = `assert mono ${ok ? 'pass' : 'fail'}`;
  assertEl.textContent = `sampleAt(505) = ${got.toFixed(3)}  · expected 50.5 ±0.01`;
}

// ─── Panel 8: the timeline ──────────────────────────────────────────────────

// The engine's timeline, not a second one. The synthetic events below go into
// the same queue the real detectors use, which is the only way this panel can
// claim to be showing how the merge behaves.

/**
 * Fake detectors, with the latencies the real ones carry.
 *
 * `latency` is read when an event is emitted, not captured here. It used to be
 * captured, and Phase 8 is exactly the situation that turns that into a lie: you
 * drag the asr latency, fire the burst, and the demo goes on using the value
 * that was in force when the page loaded.
 */
const KINDS = {
  word: { label: '"then"', latency: 'asr' },
  pow: { label: 'pow', latency: 'plosive' },
  brrr: { label: 'brrr', latency: 'trill' },
};

const lagInput = document.getElementById('lag');
const lagOut = document.getElementById('lagout');
const pendingEl = document.getElementById('pending');
const pendingN = document.getElementById('pending-n');
const releasedEl = document.getElementById('released');

lagInput.min = config.LAG_MIN;
lagInput.max = config.LAG_MAX;
lagInput.value = config.LAG;

/**
 * The slowest detector in the project. LAG below this cannot reorder reliably.
 * Recomputed rather than fixed, because Phase 8 can move any of them.
 */
const slowestDetector = () => Math.max(...Object.values(config.latency));

/**
 * One LAG, two sliders — one beside the trace where it is calibrated, one
 * beside the timeline where its effect on ordering is visible. Both write the
 * same config field, so both readouts and both positions are refreshed together
 * rather than drifting apart.
 *
 * Worth stating rather than hiding: LAG is not a taste setting with a floor of
 * "looks nice". Below the slowest detector's latency, a fast event can be
 * released before a slow one that preceded it has even been reported, and no
 * amount of correct sorting can fix that — the information had not arrived yet.
 */
/**
 * Rebuild the recogniser so a new `speech.lang` takes effect.
 *
 * `rec.lang` is read once, inside `_build()`, which only runs on `start()` — so
 * without this the language control moves, records its change, and does nothing.
 * Only Channel A stops; the camera, the DSP and the recording carry on.
 */
function relanguage() {
  if (!engine.recognition.listening) return;
  engine.recognition.stop();
  engine.recognition.start();
}

function syncLagOutputs() {
  const slowest = slowestDetector();
  const short = config.LAG < slowest;
  const text = short ? `${config.LAG} ms  · under ${slowest}` : `${config.LAG} ms`;

  for (const out of [lagOut, lag2out]) {
    out.textContent = text;
    out.style.color = short ? 'var(--hot)' : '';
  }
  lagInput.value = config.LAG;
  lag2.value = config.LAG;
  // The third slider. Refreshed rather than written, because it also tracks
  // whether the value is away from its default.
  controls.refresh();
}

// Routed through the control rack rather than writing config directly, so one
// LAG change does one thing everywhere: the field, all three sliders, the URL,
// and the line in the recording.
lagInput.addEventListener('input', () => controls.set('LAG', Number(lagInput.value)));

function emit(kind, detectedAt = performance.now()) {
  const k = KINDS[kind];
  timeline.push({
    t: detectedAt,
    detectLatency: config.latency[k.latency],
    label: k.label,
    kind,
  });
}

for (const btn of document.querySelectorAll('[data-emit]')) {
  btn.addEventListener('click', () => emit(btn.dataset.emit));
}

/**
 * The Phase 7 acceptance scenario, arriving out of order for real.
 *
 * "then" is spoken 100 ms before the "pow" — but ASR takes 300 ms to report it
 * while the plosive detector takes 15, so pow is genuinely pushed first and the
 * word turns up 185 ms later. Nothing here is staged: the reordering is done by
 * `Timeline.push`, from each event's own `detectLatency`.
 *
 * Drop LAG below 300 and the burst comes out backwards. That is not a bug in
 * the timeline — it is what happens when you try to display a moment before the
 * slowest detector has finished reporting it.
 */
document.getElementById('burst').addEventListener('click', () => {
  emit('pow', performance.now()); // spoken ~15 ms ago
  setTimeout(() => emit('word', performance.now()), 185); // spoken ~115 ms before that
});

document.getElementById('reset').addEventListener('click', () => {
  timeline.clear();
  releasedEl.replaceChildren();
});

function row(label, meta) {
  const li = document.createElement('li');
  const a = document.createElement('span');
  a.className = 'label';
  a.textContent = label;
  const b = document.createElement('span');
  b.className = 'meta';
  b.textContent = meta;
  li.append(a, b);
  return li;
}

function emptyRow(text) {
  const li = document.createElement('li');
  li.className = 'empty';
  li.textContent = text;
  return li;
}

let releasedCount = 0;

/**
 * Panels repaint at this rate, not at the display's.
 *
 * Rule 3 of the architecture section says analysers write to buffers and the
 * renderer reads on rAF. It does not say the *instrument panel* has to. Between
 * two meter racks, a status line, three check rows and two list panels, this
 * page was performing something like fifty DOM writes and a full list rebuild
 * every frame — and when Phase 4 added the second rack the whole loop fell from
 * 30 Hz to 13 Hz, which is a frame rate the vision features cannot afford.
 *
 * Nothing measured is throttled. Feature extraction, peak-hold accumulation, the
 * trace and the caption all still run every frame. Only the numbers a person
 * reads are repainted at a rate a person can read them.
 */
const PANEL_HZ = 10;
let lastPanelAt = 0;

/**
 * One gesture out of the timeline, in spoken order.
 *
 * Placed on the caption line at the moment it happened, and logged. The engine
 * does the releasing — this is only what the lab does with the result, and the
 * piece at /perform does the first half of it and nothing else.
 */
function onRelease(e) {
  releasedCount++;
  if (e.type) caption.addGesture(e);
  releasedEl.prepend(
    row(`${String(releasedCount).padStart(2, '0')}  ${e.label}`, `spoken at ${(e.t - t0) | 0} ms`),
  );
  while (releasedEl.childElementCount > 40) releasedEl.lastElementChild.remove();
}

/**
 * Draw one frame of instrument panel.
 *
 * Called by the engine, which owns the only render loop on the page and has
 * already done the measuring: watchdogs, feature extraction, classification and
 * the timeline release all happened before this runs. Everything here is
 * display.
 */
function onFrame({ now, audioFrame }) {
  const panels = now - lastPanelAt >= 1000 / PANEL_HZ;
  if (panels) lastPanelAt = now;

  // Vision runs on its own frame callback; this only draws what it produced, so
  // the renderer never blocks feature extraction.
  if (landmarker.state === 'running') {
    drawOverlay(landmarker.lastLandmarks);
    readout.update(mouthBuffer.latest(), { draw: panels });
    if (panels) updateVisionNote();
  }

  if (capture.active) {
    if (audioFrame) {
      // Extremes from every frame — that is what peak-hold is for — but the bars
      // repaint on the panel tick.
      audioReadout.update(audioFrame, {
        hold: audioFrame.db >= capture.noiseFloor.gate,
        draw: panels,
      });
      if (panels) renderChecks();
    }
    if (panels) {
      trillReadout.update(capture.envelope.latest, { hold: false });
      renderTrillHops();
      updateTrillNote();
      updateAudioNote();
    }
  }

  // The calibration instrument. Drawn every frame regardless of the camera, so
  // an empty trace is itself a reading — the mouth buffer has nothing in it.
  trace.draw({ mouthBuffer, now, words: transcript.words.slice(-24) });

  // The guided take, ticked on the same clock as the samples it is labelling.
  renderGuide(guide.tick(now));

  if (panels) {
    if (recorder.recording) {
      recState.textContent =
        `● ${(recorder.elapsed(now) / 1000).toFixed(1)} s · ` +
        `${recorder.sampleCount} mouth · ${recorder.audioCount} audio · ${recorder.envelopeCount} env`;
    } else if (engine.capturing) {
      recState.textContent = 'ready';
    }
  }

  // Rebuilding this list every frame was one of the more expensive things on
  // the page, and it describes a queue that changes a few times a second.
  if (panels) {
    const queue = timeline.toArray();
    pendingN.textContent = String(queue.length);
    pendingEl.replaceChildren(
      ...(queue.length
        ? queue.map((e) =>
            row(
              e.label,
              `onset ${(e.t - t0) | 0} ms · in ${Math.max(0, (e.t + config.LAG - now) | 0)} ms`,
            ),
          )
        : [emptyRow('nothing queued — fire the burst')]),
    );
  }
}

onQuery();
syncLagOutputs();
releasedEl.replaceChildren(emptyRow('nothing released yet'));

// The one render loop on the page. Everything above hangs off it.
engine.start();
